import { useState } from 'react';
import { Button, Card, CardContent } from '@jeraisonne/ui';
import { ShieldAlert, Users, X, UserPlus } from 'lucide-react';
import { AvatarCircle } from '@/components/avatar-circle';
import { useMyPeers, useRevokePeerLink } from '@/lib/student/peers';
import { filterByQuery, hasSearchQuery } from '@/lib/contact-search';
import { UserSafetySheet } from '@/features/safety/user-safety-sheet';

interface Props {
  studentId: string;
  onAddPeer?: () => void;
  /** Recherche de la page « Mes contacts » : ne montre que les camarades correspondants. */
  filter?: string;
}

export function PeersSection({ studentId, onAddPeer, filter = '' }: Props) {
  const { data: peers = [], isLoading } = useMyPeers(studentId);
  const revoke = useRevokePeerLink(studentId);
  const shown = filterByQuery(peers, filter, (peer) => [peer.display_name]);
  // Signaler / bloquer un camarade (0604, App Review 1.2).
  const [safetyFor, setSafetyFor] = useState<{ id: string; name: string } | null>(null);

  // Pendant une recherche, une liste sans résultat disparaît (pas de faux « Aucun camarade »).
  if (!isLoading && hasSearchQuery(filter) && shown.length === 0) return null;

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-ink-400 text-xs font-semibold uppercase tracking-wider">
          Mes camarades ({shown.length})
        </p>
        {onAddPeer && peers.length > 0 && (
          <button
            type="button"
            onClick={onAddPeer}
            className="text-primary-600 hover:text-primary-700 -my-2 inline-flex min-h-[2.75rem] items-center gap-1 text-xs font-semibold"
          >
            <UserPlus className="h-3.5 w-3.5" />
            Ajouter
          </button>
        )}
      </div>
      {isLoading ? (
        <div className="space-y-2">
          {[1, 2].map((i) => (
            <Card key={i} className="border-ink-100 animate-pulse">
              <CardContent className="flex items-center gap-3 py-3">
                <div className="bg-ink-100 h-10 w-10 rounded-full" />
                <div className="bg-ink-100 h-4 w-32 rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : peers.length === 0 ? (
        <Card className="border-ink-100 border-dashed">
          <CardContent className="space-y-4 py-6 text-center">
            <div>
              <Users className="text-ink-300 mx-auto mb-2 h-8 w-8" />
              <p className="text-ink-500 text-sm font-medium">Aucun camarade ajouté</p>
            </div>
            <div className="text-ink-500 mx-auto max-w-xs space-y-1.5 text-left text-xs">
              <p className="text-ink-700 font-semibold">Comment ajouter un camarade ?</p>
              <ol className="list-decimal space-y-1 pl-4">
                <li>Touche « Un camarade » (ou montre ton QR code).</li>
                <li>
                  Partage ton <strong>code</strong>, ton <strong>lien</strong> ou ton{' '}
                  <strong>QR code</strong>, ou entre le code de ton camarade.
                </li>
                <li>Dès qu'il valide, il apparaît ici.</li>
              </ol>
            </div>
            {onAddPeer && (
              <Button
                type="button"
                onClick={onAddPeer}
                size="sm"
                className="mx-auto w-full max-w-xs gap-1.5"
              >
                <UserPlus className="h-4 w-4" />
                Inviter un camarade
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {shown.map((peer) => (
            <Card key={peer.linkId} className="border-ink-100 hover:bg-ink-50/40 transition-colors">
              <CardContent className="flex items-center gap-3 py-3">
                <AvatarCircle name={peer.display_name} src={peer.avatar_url} size="sm" />
                <p className="text-ink-900 min-w-0 flex-1 truncate text-sm font-semibold">
                  {peer.display_name}
                </p>
                <button
                  type="button"
                  onClick={() => setSafetyFor({ id: peer.id, name: peer.display_name })}
                  className="text-ink-400 hover:text-ink-700 -my-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-full transition-colors"
                  aria-label={`Signaler ou bloquer ${peer.display_name}`}
                >
                  <ShieldAlert className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (confirm(`Retirer ${peer.display_name} de tes camarades ?`)) {
                      revoke.mutate(peer.id);
                    }
                  }}
                  disabled={revoke.isPending}
                  className="text-ink-400 -my-2 -mr-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-full transition-colors hover:text-red-500"
                  aria-label={`Retirer ${peer.display_name}`}
                >
                  <X className="h-4 w-4" />
                </button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      {safetyFor && (
        <UserSafetySheet
          userId={safetyFor.id}
          name={safetyFor.name}
          onClose={() => setSafetyFor(null)}
        />
      )}
    </section>
  );
}
